import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useSnapshotStore } from "@/stores/snapshotStore";
import { toast } from "sonner";

interface CreateSnapshotDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateSnapshotDialog({ open, onOpenChange }: CreateSnapshotDialogProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const { createSnapshot, isLoading, storagePercentage } = useSnapshotStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    // Check storage limit
    if (storagePercentage >= 100) {
      toast.error('Storage limit reached. Delete old snapshots to free up space.');
      return;
    }

    try {
      await createSnapshot(name, description, 'manual');
      setName("");
      setDescription("");
      onOpenChange(false);
    } catch (error) {
      // Error is handled in the store
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save Checkpoint</DialogTitle>
          <DialogDescription>
            Create a named checkpoint of your current data. You can restore to this point later.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Checkpoint Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Before Q4 Changes"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                placeholder="Add notes about what changed or why you're saving..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || !name.trim()}>
              {isLoading ? 'Saving...' : 'Save Checkpoint'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
